//src/components/admin/SettingsManagement.tsx
import React, { useState, useEffect } from 'react';
import { 
  Settings, Save, RefreshCw, AlertCircle, CheckCircle, 
  Mail, Globe, Shield, Database, Bell, Zap, Key,
  Server, Users, CreditCard, FileText, Lock, Eye, EyeOff,
  Wrench, Monitor, Activity, HelpCircle
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import { AiMatchingToggle } from './AiMatchingToggle';

interface SystemSettings {
  maintenance_mode: boolean;
  max_api_requests_per_minute: number;
  enable_user_registration: boolean;
  default_subscription_tier: string;
  system_email: string;
  support_email: string;
  platform_name: string;
  max_monthly_views_free: number;
  max_monthly_views_basic: number;
  max_monthly_views_pro: number;
  enhanced_proposals_enabled: boolean;
  ai_features_enabled: boolean;
  stripe_webhook_enabled: boolean;
  email_notifications_enabled: boolean;
  analytics_enabled: boolean;
}

interface SettingsSection {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  fields: SettingField[];
}

interface SettingField {
  key: keyof SystemSettings;
  label: string;
  type: 'text' | 'email' | 'number' | 'boolean' | 'select';
  description?: string;
  options?: { value: string; label: string }[];
  validation?: (value: any) => string | null;
}

export function SettingsManagement() {
  const [settings, setSettings] = useState<SystemSettings>({
    maintenance_mode: false,
    max_api_requests_per_minute: 100,
    enable_user_registration: true,
    default_subscription_tier: 'free',
    system_email: 'admin@bidwin.com',
    support_email: 'support@bidwin.com',
    platform_name: 'BidWin',
    max_monthly_views_free: 10,
    max_monthly_views_basic: 50,
    max_monthly_views_pro: 200,
    enhanced_proposals_enabled: true,
    ai_features_enabled: true,
    stripe_webhook_enabled: false,
    email_notifications_enabled: true,
    analytics_enabled: true
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('general');
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      
      // Try to load from system_settings table
      const { data } = await supabase
        .from('system_settings')
        .select('*')
        .limit(1)
        .maybeSingle();

      if (data) {
        setSettings(prev => ({ ...prev, ...data }));
      } else {
        console.warn('No system settings found, using defaults');
      }
    } catch (error) {
      console.warn('Could not load system settings:', error);
      toast.info('Using default settings - save to create system configuration');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);

      // First try to update existing settings
      const { data: existingSettings } = await supabase
        .from('system_settings')
        .select('id')
        .limit(1)
        .maybeSingle();

      if (existingSettings) {
        // Update existing
        const { error } = await supabase
          .from('system_settings')
          .update({
            ...settings,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingSettings.id);

        if (error) throw error;
      } else {
        // Create new
        const { error } = await supabase
          .from('system_settings')
          .insert([{
            ...settings,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }]);

        if (error) throw error;
      }

      setUnsavedChanges(false);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = <K extends keyof SystemSettings>(key: K, value: SystemSettings[K]) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setUnsavedChanges(true);
  };

  const testEmailSettings = async () => {
    toast.info('Testing email configuration...');
    // Mock email test - in real implementation, this would send a test email
    setTimeout(() => {
      toast.success('Email configuration test completed');
    }, 2000);
  };

  const resetToDefaults = () => {
    if (confirm('Are you sure you want to reset all settings to defaults? This cannot be undone.')) {
      setSettings({
        maintenance_mode: false,
        max_api_requests_per_minute: 100,
        enable_user_registration: true,
        default_subscription_tier: 'free',
        system_email: 'admin@bidwin.com',
        support_email: 'support@bidwin.com',
        platform_name: 'BidWin',
        max_monthly_views_free: 10,
        max_monthly_views_basic: 50,
        max_monthly_views_pro: 200,
        enhanced_proposals_enabled: true,
        ai_features_enabled: true,
        stripe_webhook_enabled: false,
        email_notifications_enabled: true,
        analytics_enabled: true
      });
      setUnsavedChanges(true);
      toast.info('Settings reset to defaults - remember to save');
    }
  };

  const settingsSections: SettingsSection[] = [
    {
      id: 'general',
      title: 'General Settings',
      description: 'Basic platform configuration',
      icon: Settings,
      fields: [
        {
          key: 'platform_name',
          label: 'Platform Name',
          type: 'text',
          description: 'Display name for your platform'
        },
        {
          key: 'maintenance_mode',
          label: 'Maintenance Mode',
          type: 'boolean',
          description: 'Put the platform in maintenance mode'
        },
        {
          key: 'enable_user_registration',
          label: 'User Registration',
          type: 'boolean',
          description: 'Allow new users to register'
        },
        {
          key: 'default_subscription_tier',
          label: 'Default Subscription Tier',
          type: 'select',
          description: 'Default tier for new users',
          options: [
            { value: 'free', label: 'Free' },
            { value: 'basic', label: 'Basic' },
            { value: 'pro', label: 'Pro' },
            { value: 'enterprise', label: 'Enterprise' }
          ]
        }
      ]
    },
    {
      id: 'email',
      title: 'Email Configuration',
      description: 'Email settings and notifications',
      icon: Mail,
      fields: [
        {
          key: 'system_email',
          label: 'System Email',
          type: 'email',
          description: 'Email address for system notifications'
        },
        {
          key: 'support_email',
          label: 'Support Email',
          type: 'email',
          description: 'Email address for customer support'
        },
        {
          key: 'email_notifications_enabled',
          label: 'Email Notifications',
          type: 'boolean',
          description: 'Enable email notifications for users'
        }
      ]
    },
    {
      id: 'limits',
      title: 'Usage Limits',
      description: 'Configure tier-based usage limits',
      icon: Shield,
      fields: [
        {
          key: 'max_monthly_views_free',
          label: 'Free Tier Monthly Views',
          type: 'number',
          description: 'Maximum monthly contract views for free users'
        },
        {
          key: 'max_monthly_views_basic',
          label: 'Basic Tier Monthly Views',
          type: 'number',
          description: 'Maximum monthly contract views for basic users'
        },
        {
          key: 'max_monthly_views_pro',
          label: 'Pro Tier Monthly Views',
          type: 'number',
          description: 'Maximum monthly contract views for pro users'
        },
        {
          key: 'max_api_requests_per_minute',
          label: 'API Rate Limit',
          type: 'number',
          description: 'Maximum API requests per minute per user'
        }
      ]
    },
    {
      id: 'features',
      title: 'Feature Toggles',
      description: 'Enable or disable platform features',
      icon: Zap,
      fields: [
        {
          key: 'enhanced_proposals_enabled',
          label: 'Enhanced Proposals',
          type: 'boolean',
          description: 'Enable AI-powered enhanced proposal generation'
        },
        {
          key: 'ai_features_enabled',
          label: 'AI Features',
          type: 'boolean',
          description: 'Enable all AI-powered features'
        },
        {
          key: 'analytics_enabled',
          label: 'Analytics Tracking',
          type: 'boolean',
          description: 'Enable platform analytics and tracking'
        },
        {
          key: 'stripe_webhook_enabled',
          label: 'Stripe Webhooks',
          type: 'boolean',
          description: 'Enable Stripe webhook processing'
        }
      ]
    }
  ];

  const renderField = (field: SettingField) => {
    const value = settings[field.key];

    switch (field.type) {
      case 'boolean':
        return (
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => updateSetting(field.key, !value as any)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-bw-primary focus:ring-offset-2 ${
                value ? 'bg-bw-primary' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-bw-surface shadow ring-0 transition duration-200 ease-in-out ${
                  value ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        );

      case 'select':
        return (
          <select
            value={value as string}
            onChange={(e) => updateSetting(field.key, e.target.value as any)}
            className="mt-1 block w-full rounded-md border-bw-border shadow-sm focus:border-bw-primary focus:ring-bw-primary sm:text-sm"
          >
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'number':
        return (
          <input
            type="number"
            value={value as number}
            onChange={(e) => updateSetting(field.key, parseInt(e.target.value) || 0 as any)}
            className="mt-1 block w-full rounded-md border-bw-border shadow-sm focus:border-bw-primary focus:ring-bw-primary sm:text-sm"
          />
        );

      default:
        return (
          <input
            type={field.type}
            value={value as string}
            onChange={(e) => updateSetting(field.key, e.target.value as any)}
            className="mt-1 block w-full rounded-md border-bw-border shadow-sm focus:border-bw-primary focus:ring-bw-primary sm:text-sm"
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-servet-blue"></div>
          <p className="text-bw-muted mt-4">Loading settings...</p>
        </div>
      </div>
    );
  }

  const activeSettingsSection = settingsSections.find(s => s.id === activeSection) || settingsSections[0];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-bw-ink">Settings Management</h2>
          <p className="text-bw-muted mt-1">Configure platform settings and system parameters</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={resetToDefaults}
            className="flex items-center px-3 py-2 bg-bw-surface-subtle text-bw-ink rounded-md hover:bg-gray-200"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset
          </button>
          <button
            onClick={saveSettings}
            disabled={!unsavedChanges || saving}
            className={`flex items-center px-4 py-2 rounded-md ${
              unsavedChanges && !saving
                ? 'bg-bw-primary text-white hover:bg-bw-primary/90'
                : 'bg-gray-300 text-bw-muted cursor-not-allowed'
            }`}
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* AI Contract Matching master switch (self-contained, persists immediately) */}
      <AiMatchingToggle />

      {/* Unsaved Changes Alert */}
      {unsavedChanges && (
        <div className="bg-fit-moderate-bg border border-fit-moderate-border rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-fit-moderate mr-2" />
            <p className="text-fit-moderate">You have unsaved changes. Don't forget to save your settings.</p>
          </div>
        </div>
      )}

      {/* Maintenance Mode Warning */}
      {settings.maintenance_mode && (
        <div className="bg-urgent-bg border border-urgent-border rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-urgent mr-2" />
            <p className="text-urgent">Platform is currently in maintenance mode. Users cannot access the system.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Settings Navigation */}
        <div className="lg:col-span-1">
          <nav className="space-y-1">
            {settingsSections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                  activeSection === section.id
                    ? 'bg-bw-primary text-white'
                    : 'text-bw-ink hover:bg-bw-surface-subtle'
                }`}
              >
                <section.icon className="h-4 w-4 mr-3" />
                {section.title}
              </button>
            ))}
          </nav>

          {/* Quick Actions */}
          <div className="mt-6 space-y-2">
            <button
              disabled
              title="Email sending is not yet wired up"
              className="w-full flex items-center px-3 py-2 text-sm bg-bw-surface-subtle text-bw-muted rounded-md cursor-not-allowed opacity-70"
            >
              <Mail className="h-4 w-4 mr-2" />
              Test Email <span className="ml-1.5 text-xs">(soon)</span>
            </button>
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="w-full flex items-center px-3 py-2 text-sm bg-bw-surface-subtle text-bw-ink rounded-md hover:bg-gray-200"
            >
              {showAdvanced ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
              {showAdvanced ? 'Hide Advanced' : 'Show Advanced'}
            </button>
          </div>
        </div>

        {/* Settings Content */}
        <div className="lg:col-span-3">
          <div className="bg-bw-surface shadow-card rounded-lg">
            <div className="px-6 py-4 border-b border-bw-border">
              <div className="flex items-center">
                <activeSettingsSection.icon className="h-5 w-5 text-bw-primary mr-2" />
                <div>
                  <h3 className="text-lg font-medium text-bw-ink">{activeSettingsSection.title}</h3>
                  <p className="text-sm text-bw-muted">{activeSettingsSection.description}</p>
                </div>
              </div>
            </div>

            <div className="px-6 py-6">
              <div className="space-y-6">
                {activeSettingsSection.fields.map((field) => (
                  <div key={field.key}>
                    <label className="block text-sm font-medium text-bw-ink">
                      {field.label}
                    </label>
                    {renderField(field)}
                    {field.description && (
                      <p className="mt-1 text-sm text-bw-muted">{field.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Advanced Settings */}
          {showAdvanced && (
            <div className="mt-6 bg-bw-surface shadow-card rounded-lg">
              <div className="px-6 py-4 border-b border-bw-border">
                <div className="flex items-center">
                  <Wrench className="h-5 w-5 text-fit-moderate mr-2" />
                  <div>
                    <h3 className="text-lg font-medium text-bw-ink">Advanced Settings</h3>
                    <p className="text-sm text-bw-muted">Advanced configuration options - use with caution</p>
                  </div>
                </div>
              </div>

              <div className="px-6 py-6">
                <div className="bg-fit-moderate-bg border border-fit-moderate-border rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-fit-moderate mr-2" />
                    <p className="text-fit-moderate text-sm">
                      These settings can affect platform stability. Only modify if you understand the implications.
                    </p>
                  </div>
                </div>

                <p className="text-xs text-bw-muted mb-3 italic">
                  These are managed at the infrastructure level (Supabase) and are shown here for reference only — they are not yet editable from this screen.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-bw-muted">Database Connection Pool Size</label>
                    <input
                      type="number"
                      value={10}
                      disabled
                      readOnly
                      className="mt-1 block w-full rounded-md border-bw-border bg-bw-surface-subtle text-bw-muted shadow-sm sm:text-sm cursor-not-allowed"
                    />
                    <p className="mt-1 text-sm text-bw-muted">Maximum database connections (read-only)</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-bw-muted">Cache TTL (seconds)</label>
                    <input
                      type="number"
                      value={3600}
                      disabled
                      readOnly
                      className="mt-1 block w-full rounded-md border-bw-border bg-bw-surface-subtle text-bw-muted shadow-sm sm:text-sm cursor-not-allowed"
                    />
                    <p className="mt-1 text-sm text-bw-muted">Time to live for cached data (read-only)</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}